import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@hipotecar/ui/input";
import { Badge } from "@hipotecar/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
} from "@/components/ui/select";
import MultiSelect from "@/components/composed/MultiSelect";
import LinkedNumericInput from "@/components/composed/LinkedNumericInput";
import EqualSignInput from "@/components/composed/EqualSignInput";
import { getCurrencySymbol } from "@/utils/finance";
import { formatCurrency } from "@/utils/formatters";
import type { MortgageFormInput } from "@features/mortgages/schemas/mortgage";
import type { PropertyResponse } from "@/features/properties";
import {
	getMonthOptions,
	getCuotasOptions,
	BANK_OPTIONS,
	getCreditTypeOptions,
} from "@features/mortgages/utils/formOptions";
import { MONEDAS, type Moneda } from "@/schemas/common";
import { getValidPriceRange } from "@features/mortgages/utils/calculations";
import { useMortgageFormContext } from "../../useMortgageFormContext";
import BonoMiviviendaSelector, { BonoType } from "./BonoMiviviendaSelector";


const MONEDA_SELECT_ITEMS = MONEDAS.map((moneda) => (
	<SelectItem key={moneda} value={moneda}>
		{moneda}
	</SelectItem>
));

interface PrincipalTabProps {
	property: PropertyResponse;
	downPaymentPercent: number;
	onPercentChange: (percent: number) => void;
	onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onFechaDesembolsoChange: (date: Date | undefined) => void;
	termYears: number;
	onYearsChange: (years: number) => void;
	effectivePropertyPrice: number;
}

export const PrincipalTab: React.FC<PrincipalTabProps> = ({
	property,
	downPaymentPercent,
	onPercentChange,
	onAmountChange,
	onFechaDesembolsoChange,
	termYears,
	onYearsChange,
	effectivePropertyPrice,
}) => {
	const { control, watch, setValue } = useFormContext<MortgageFormInput>();
	const { isLoading, isReadOnly } = useMortgageFormContext();
	const { t, i18n } = useTranslation(["common", "hipotecas"]);

	const cuotas_dobles = watch("cuotas_dobles");
	const selectedMoneda = watch("moneda");
	const currencySymbol = getCurrencySymbol(selectedMoneda || property.moneda);
	const disbursementDate = watch("fecha_desembolso");

	// Use centralized form options
	const monthOptions = getMonthOptions(t, i18n.language);
	const cuotasOptions = getCuotasOptions(t);
	const creditTypeOptions = getCreditTypeOptions(t);
	const creditType = watch("tipo_credito");
	const isTransitionLinkedMortgage = Boolean(watch("id_hipoteca_origen"));

	// Bono handlers
	const currentBonoType = watch("tipo_bono") || "no_bono";
	const currentBonoValue = watch("bono") || 0;

	const getBonoType = (): BonoType => {
		if (currentBonoType === "verde") return "verde";
		if (currentBonoType === "buen_pagador") return "buen_pagador";
		if (currentBonoType === "otro") return "otro";
		return "no_bono";
	};

	const handleBonoChange = (type: BonoType, bonus: number) => {
		setValue("bono", bonus, { shouldValidate: true });
		setValue("tipo_bono", type, { shouldValidate: true });
		if (!watch("id_hipoteca_origen")) {
			const currentInicial = Number(watch("inicial")) || 0;
			const newMonto = effectivePropertyPrice - bonus - currentInicial;
			setValue("monto_prestamo", Number(Math.max(0, newMonto).toFixed(2)));
		}
	};



	// Handle Double Quotas toggle and defaults
	const handleCuotasDoblesChange = (isDoble: boolean) => {
		setValue("cuotas_dobles", isDoble, { shouldValidate: true });
		if (isDoble) {
			const current = watch("meses_cuota_doble");
			if (!current || current.length === 0) {
				setValue("meses_cuota_doble", [7, 12], { shouldValidate: true });
			}
		} else {
			setValue("meses_cuota_doble", [], { shouldValidate: true });
		}
	};

	const isMiViviendaDisabled = (selectedMoneda || property.moneda) !== "PEN";

	const acquisitionYear = useMemo(() => {
		const acqDate = disbursementDate || property.fecha_adquisicion;
		if (!acqDate) return new Date().getFullYear();
		const yearMatch = acqDate.match(/^(\d{4})/);
		if (yearMatch) {
			return parseInt(yearMatch[1], 10);
		}
		return new Date(acqDate).getFullYear();
	}, [disbursementDate, property.fecha_adquisicion]);

	const priceRange = useMemo(() => {
		return getValidPriceRange(acquisitionYear);
	}, [acquisitionYear]);

	const minBonoPrice = priceRange?.min || 0;
	const maxBonoPrice = priceRange?.max || 0;

	const isEligibleMivivienda =
		!isMiViviendaDisabled &&
		priceRange &&
		Number(property.precio_compra) >= minBonoPrice &&
		Number(property.precio_compra) <= maxBonoPrice;

	return (
		<div className="space-y-8">
			{/* Section title */}
			<h3 className="text-lg font-normal text-gray-900 dark:text-white">
				{t("hipotecas:create.form.mortgage_info_title")}
			</h3>

			{/* Loan Amount (summary) + average rates alert */}
			<div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
				<div className="flex flex-col gap-1.5">
					<label className="text-sm font-medium text-gray-500 dark:text-gray-400">
						{t("hipotecas:create.form.loan_amount")}
					</label>
					<div className="mt-1 text-2xl text-white">
						{formatCurrency(
							watch("monto_prestamo"),
							selectedMoneda || property.moneda,
						)}
					</div>
					<div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
						<span className="font-medium text-gray-400">=</span>
						{isTransitionLinkedMortgage
							? t("hipotecas:create.form.loan_amount_formula_transition")
							: t("hipotecas:create.form.loan_amount_formula")}
					</div>
				</div>
				<Alert variant="warning">
					<AlertTriangle className="size-5" />
					<AlertDescription>
						{t("hipotecas:create.form.average_rates_alert")}
					</AlertDescription>
				</Alert>
			</div>

			{/* Row: Nombre | Moneda */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<FormField
					control={control}
					name="nombre"
					render={({ field }) => (
						<FormItem>
							<FormLabel required>{t("hipotecas:create.form.name")}</FormLabel>
							<FormControl>
								<Input
									placeholder={t("hipotecas:create.form.name_placeholder")}
									disabled={isLoading || isReadOnly}
									{...field}
									value={field.value ?? ""}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={control}
					name="moneda"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t("hipotecas:create.form.moneda")}</FormLabel>
							<Select
								onValueChange={(value) => {
									field.onChange(value);
								}}
								value={field.value}
								disabled={isLoading || isReadOnly}
							>
								<FormControl>
									<SelectTrigger>
										<SelectValue placeholder={t("common:select_placeholder")} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{MONEDA_SELECT_ITEMS}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>

			{/* Row: Fecha de Desembolso | Crédito MiVivienda */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<FormField
					control={control}
					name="fecha_desembolso"
					render={({ field }) => (
						<FormItem>
							<FormLabel required>
								{t("hipotecas:create.form.disbursement_date")}
							</FormLabel>
							<FormControl>
								<DatePicker
									date={
										field.value
											? new Date(field.value + "T00:00:00")
											: undefined
									}
									minDate={
										property.fecha_adquisicion
											? new Date(property.fecha_adquisicion + "T00:00:00")
											: undefined
									}
									setDate={onFechaDesembolsoChange}
									disabled={
										isLoading || isTransitionLinkedMortgage || isReadOnly
									}
									className="w-full"
								/>
							</FormControl>
							<p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
								{t("hipotecas:create.form.disbursement_date_helper")}
							</p>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={control}
					name="tipo_credito"
					render={({ field }) => (
						<FormItem>
							<div className="flex min-h-[20px] items-center gap-2">
								<FormLabel>
									{t("hipotecas:create.form.credit_mivivienda")}
								</FormLabel>
								{!isMiViviendaDisabled && (
									<Badge
										variant="light"
										color={isEligibleMivivienda ? "success" : "warning"}
										size="sm"
										className="shrink-0 whitespace-nowrap"
									>
										{isEligibleMivivienda
											? t(
													"propiedades:create.form.bono_mivivienda_status_califica",
												)
											: t(
													"propiedades:create.form.bono_mivivienda_status_fuera_rango",
												)}
									</Badge>
								)}
							</div>
							<Select
								onValueChange={(value) => {
									field.onChange(value);
									if (!isTransitionLinkedMortgage && value !== "mi_vivienda") {
										setValue("bono", 0);
										setValue("tipo_bono", "no_bono");
									}
								}}
								value={field.value ?? "tradicional"}
								disabled={isLoading || isTransitionLinkedMortgage || isReadOnly}
							>
								<FormControl>
									<SelectTrigger>
										<SelectValue placeholder={t("common:select_placeholder")} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{creditTypeOptions.map((option) => (
										<SelectItem
											key={option.value}
											value={option.value}
											disabled={
												option.value === "mi_vivienda" && isMiViviendaDisabled
											}
										>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>

			{/* Bono MiVivienda Selector (Conditional) */}
			{creditType === "mi_vivienda" && (
				<BonoMiviviendaSelector
					price={effectivePropertyPrice}
					moneda={(selectedMoneda || property.moneda) as Moneda}
					acquisitionDate={disbursementDate || property.fecha_adquisicion}
					selectedType={getBonoType()}
					manualBonus={Number(currentBonoValue) || 0}
					onChange={handleBonoChange}
					showOtro={true}
					gridCols={4}
					isReadOnly={isReadOnly || isTransitionLinkedMortgage}
				/>
			)}

			{/* Row: Banco | Tasa de Interés (TEA) */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<FormField
					control={control}
					name="entidad_financiera"
					render={({ field }) => (
						<FormItem>
							<FormLabel required>{t("hipotecas:create.form.bank")}</FormLabel>
							<Select
								onValueChange={field.onChange}
								value={field.value}
								disabled={isLoading || isReadOnly}
							>
								<FormControl>
									<SelectTrigger>
										<SelectValue placeholder={t("common:select_placeholder")} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{BANK_OPTIONS.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={control}
					name="tasa_interes"
					render={({ field }) => (
						<FormItem>
							<FormLabel required>
								{t("hipotecas:create.form.interest_rate_tea")}
							</FormLabel>
							<FormControl>
								<div className="relative">
									<Input
										type="number"
										step="0.001"
										disabled={isLoading || isReadOnly}
										{...field}
										value={field.value ?? ""}
										onChange={(e) =>
											field.onChange(parseFloat(e.target.value) || 0)
										}
										className="pr-20"
										onFocus={(e) => e.target.select()}
									/>
									<span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs text-gray-400">
										{t("hipotecas:create.form.annual_suffix")}
									</span>
								</div>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>

			{/* Down Payment (Cuota Inicial) */}
			<LinkedNumericInput
				label={t("hipotecas:create.form.down_payment")}
				required={true}
				leftValue={downPaymentPercent}
				leftSuffix="% del precio"
				leftMax={80}
				onLeftChange={onPercentChange}
				rightFieldName="inicial"
				rightPrefix={currencySymbol}
				control={control}
				onRightChange={onAmountChange}
				disabled={isLoading || isTransitionLinkedMortgage || isReadOnly}
			/>

			{/* Plazo */}
			<EqualSignInput
				label={t("hipotecas:create.form.term")}
				required={true}
				leftValue={termYears}
				leftSuffix={t("hipotecas:create.form.years_label")}
				onLeftChange={onYearsChange}
				rightFieldName="plazo_meses"
				rightSuffix={t("hipotecas:create.form.months_label")}
				control={control}
				conversionFactor={12}
				disabled={isLoading || isReadOnly}
			/>

			{/* Row: Cuotas por Año | Meses con Cuota Doble */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<FormField
					control={control}
					name="cuotas_dobles"
					render={({ field }) => (
						<FormItem>
							<FormLabel required>
								{t("hipotecas:create.form.payments_per_year")}
							</FormLabel>
							<Select
								onValueChange={(value) =>
									handleCuotasDoblesChange(value === "doble")
								}
								value={field.value ? "doble" : "regular"}
								disabled={isLoading || isReadOnly}
							>
								<FormControl>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{cuotasOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				{cuotas_dobles && (
					<FormField
						control={control}
						name="meses_cuota_doble"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("hipotecas:create.form.double_payment_months")}
								</FormLabel>
								<MultiSelect
									label=""
									options={monthOptions}
									value={((field.value as number[]) || []).map(String)}
									onChange={(vals) => field.onChange(vals.map(Number))}
									disabled={isLoading || isReadOnly}
									placeholder={t(
										"hipotecas:create.form.double_payment_months_placeholder",
									)}
								/>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}
			</div>
		</div>
	);
};
